import React from 'react';
import { Box, Text } from '@cowbell/ui';
import { faCircleCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { rem } from '../../../components/panels/panelLayout';
import { useDashboardPanelTokens } from './dashboardPanelTokens';
import type { RiskFindings } from './riskFindings';

/**
 * Severity ink. Deliberately not the tile's brand ramp: severity has to read as
 * a scale at a glance, and the count beside each swatch is what carries the
 * detail, so the colours only need to be ordered and distinguishable.
 */
const SEVERITY_COLORS: Record<string, string> = {
  VERY_HIGH: '#b02a24',
  CRITICAL: '#b02a24',
  HIGH: '#c0392b',
  MEDIUM: '#b7791f',
  LOW: '#8593aa',
  INFORMATION: '#8593aa',
  INFO: '#8593aa',
};

const colorFor = (severity: string) => SEVERITY_COLORS[severity] ?? '#8593aa';

interface RiskFindingsSummaryProps {
  readonly findings: RiskFindings;
  /**
   * Whether a report is actually on offer. The all-clear copy points the reader
   * at the report for the detail, which it may only do when there is one.
   */
  readonly reportAvailable?: boolean;
}

/**
 * What the report actually found: how much is outstanding, how it breaks down
 * by severity, and the most severe items -- so the tile answers "what do I need
 * to do?" rather than only "what is this report?".
 */
export function RiskFindingsSummary({
  findings,
  reportAvailable = false,
}: Readonly<RiskFindingsSummaryProps>) {
  const tokens = useDashboardPanelTokens();

  if (findings.total === 0) {
    return <AllClear reportAvailable={reportAvailable} />;
  }

  // Only shown when the fetched page covered every finding: a bar summing to
  // less than the headline reads as broken arithmetic rather than as a partial
  // view.
  const showBreakdown =
    findings.isComplete &&
    findings.bySeverity.reduce((sum, entry) => sum + entry.count, 0) > 0;

  return (
    <Box data-qa="RISK_FINDINGS_SUMMARY" style={{ marginTop: rem(10) }}>
      <Box style={{ display: 'flex', alignItems: 'baseline', gap: rem(8) }}>
        <Text
          component="span"
          style={{
            fontSize: rem(34),
            fontWeight: 700,
            lineHeight: 1,
            letterSpacing: '-0.02em',
            fontVariantNumeric: 'tabular-nums',
            color: tokens.ink,
          }}
        >
          {findings.total}
        </Text>
        <Text
          component="span"
          style={{ fontSize: rem(13), color: tokens.inkFaint }}
        >
          open {findings.total === 1 ? 'finding' : 'findings'} to remediate
        </Text>
      </Box>

      {showBreakdown && (
        <>
          <Box
            aria-hidden="true"
            style={{
              display: 'flex',
              marginTop: rem(10),
              height: rem(6),
              borderRadius: rem(999),
              overflow: 'hidden',
            }}
          >
            {findings.bySeverity.map((entry) => (
              <Box
                key={entry.severity}
                style={{
                  // Proportional to the counted rows, so the bar always fills.
                  flexGrow: entry.count,
                  backgroundColor: colorFor(entry.severity),
                }}
              />
            ))}
          </Box>

          <Box
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: rem(14),
              marginTop: rem(8),
            }}
          >
            {findings.bySeverity.map((entry) => (
              <Box
                key={entry.severity}
                style={{ display: 'flex', alignItems: 'center', gap: rem(5) }}
              >
                <Box
                  aria-hidden="true"
                  style={{
                    width: rem(7),
                    height: rem(7),
                    flexShrink: 0,
                    borderRadius: '50%',
                    backgroundColor: colorFor(entry.severity),
                  }}
                />
                <Text
                  component="span"
                  style={{
                    fontSize: rem(12),
                    fontWeight: 700,
                    color: tokens.ink,
                  }}
                >
                  {entry.count}
                </Text>
                <Text
                  component="span"
                  style={{ fontSize: rem(12), color: tokens.inkFaint }}
                >
                  {entry.label.toLowerCase()}
                </Text>
              </Box>
            ))}
          </Box>
        </>
      )}

      {findings.top.length > 0 && (
        <Box
          component="ul"
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            marginTop: rem(12),
            paddingTop: rem(12),
            borderTop: `1px solid ${tokens.line}`,
          }}
        >
          {findings.top.map((finding) => (
            <Box
              component="li"
              key={finding.id}
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto minmax(0, 1fr)',
                columnGap: rem(9),
                alignItems: 'start',
                padding: `${rem(4)} 0`,
              }}
            >
              <Box
                aria-hidden="true"
                style={{
                  width: rem(7),
                  height: rem(7),
                  marginTop: rem(5),
                  flexShrink: 0,
                  borderRadius: '50%',
                  backgroundColor: colorFor(finding.severity),
                }}
              />
              <Text
                style={{
                  fontSize: rem(12.5),
                  lineHeight: 1.4,
                  color: tokens.inkBody,
                }}
              >
                {finding.description}
                {finding.severityLabel ? (
                  <Box
                    component="span"
                    style={{ position: 'absolute', left: '-9999px' }}
                  >
                    {` (${finding.severityLabel} severity)`}
                  </Box>
                ) : null}
              </Text>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

/**
 * Nothing outstanding.
 *
 * A bare "0" under a heading reads as a tile that failed to load rather than as
 * good news, so the zero case gets its own shape: the result leads, a positive
 * band carries the meaning at a glance, and a plain line says what it rests on.
 */
function AllClear({ reportAvailable }: Readonly<{ reportAvailable: boolean }>) {
  const tokens = useDashboardPanelTokens();

  return (
    <Box data-qa="RISK_FINDINGS_ALL_CLEAR" style={{ marginTop: rem(10) }}>
      <Box style={{ display: 'flex', alignItems: 'center', gap: rem(8) }}>
        <FontAwesomeIcon
          icon={faCircleCheck}
          color={tokens.positive}
          style={{ fontSize: rem(22) }}
        />
        <Text
          component="span"
          style={{
            fontSize: rem(20),
            fontWeight: 700,
            lineHeight: 1.2,
            letterSpacing: '-0.01em',
            color: tokens.ink,
          }}
        >
          No open findings
        </Text>
      </Box>

      <Box
        aria-hidden="true"
        style={{
          marginTop: rem(12),
          height: rem(6),
          borderRadius: rem(999),
          backgroundColor: tokens.positiveSurface,
          borderLeft: `${rem(40)} solid ${tokens.positive}`,
          boxSizing: 'border-box',
        }}
      />

      <Text
        style={{
          marginTop: rem(12),
          fontSize: rem(12.5),
          lineHeight: 1.55,
          color: tokens.inkBody,
        }}
      >
        {/* Only points at the report when there is one to point at: this tile
            also says "No report available yet", and the two together would
            have contradicted each other. */}
        Your latest scan found nothing to remediate.
        {reportAvailable
          ? ' The report has the full detail behind that result.'
          : ''}
      </Text>
    </Box>
  );
}

export default RiskFindingsSummary;
